import React from 'react';
import { FormType } from 'common/components/QuickQuoteForm/types';
import TransportationComponent from './ui-components/Transportation/Transportation';
import { Slider, Rating, Prices } from './ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import styles from './SearchResult.module.scss';
import { screenStore } from 'stores/ScreenStore';
import { useResultListContext } from 'common/contexts/ResultListContext';
import SpaceInfo from './ui-components/SpaceInfo/SpaceInfo';
import { observer } from 'mobx-react-lite';

interface RatingData {
  revs: number;
  star_rating: number;
}

interface MonthlyPrice {
  name: string;
  currency_code: string;
  price: number;
  duration_metric: string;
}

interface Image {
  url: string;
  url_no_params: string;
  width: number;
  height: number;
  srcset: string;
  sizes: string;
}

interface Props {
  images: Image[];
  isPopular: boolean;
  header: string;
  rating: RatingData;
  description: string;
  currency: string;
  cs_id: number;
  resources: string[];
  view: string;
  link: string;
  setCurrentMarker: any;
  monthlyPrice?: MonthlyPrice[];
  transportation_links: any;
  country: string;
  amenities_ids: string;
  reviews_full: any;
  listingsCardPosition: number;
  listingsPageNumber: number;
}

// redo this popup as a global context
const isCenterPage: boolean = !!document.querySelector('.space-right-buttons-outer');

const openModal = async (
    formType: FormType,
    targetElement: HTMLElement,
    trackingData: {
      cs_id: number;
      isPopular: boolean;
      ratingScore: number;
      listingsCardPosition?: number;
      listingsPageNumber?: number;
    }
): Promise<void> => {
  let spaceId;
  if (isCenterPage) {
    spaceId = (document.getElementById('space_id') as HTMLInputElement)?.value;
  } else {
    const parentElement = targetElement?.parentElement;
    spaceId = parentElement?.dataset?.spaceId;
  }

  const modalProperties = {
    formType,
    spaceId,
    targetElement,
    ...trackingData,               // ✅ store additional info here
    form_step_label: 'Start',     // Optional default value
    form_step_number: 1,
  };

  // 👉 Save tracking data globally for later use
  const globalAny: any = global;
  globalAny.quickQuoteTrackingData = modalProperties;
  const openEvent = getOpenEventName(formType);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: openEvent,
    centre_id: trackingData.cs_id?.toString() ?? '0',
    popular: trackingData.isPopular ?? false,
    rating_score: trackingData.ratingScore ?? 0,
    listings_card_position: trackingData.listingsCardPosition ?? null,
    listings_page_number: trackingData.listingsPageNumber ?? null,
  });

  globalAny.toggleReactModal({ isQuickQuoteModalOpen: true }, modalProperties);
};

function getOpenEventName(formType: FormType): string {
  switch (formType) {
    case FormType.BookViewing:
      return 'book_a_tour_open';
    case FormType.GeneralEnquiry:
      return 'enquire_now_open';
    default:
      return 'quick_quote_open';
  }
}

const formatCityName = (slug: string): string => {
  if (!slug) return '';
  return slug
      .split('-')                  // split on dash
      .map(w => w.charAt(0).toUpperCase() + w.slice(1)) // Capitalize
      .join(' ');                  // join with space
};
const getCityFromPath = (): string => {
  if (typeof window === 'undefined') return '';
  const parts = window.location.pathname.split('/');
  // Example: /united-kingdom/london → ["", "united-kingdom", "london"]
  return parts[2] ? decodeURIComponent(parts[2]) : '';
};

const SearchResult: React.FC<Props> = ({
  images = [],
  isPopular = false,
  header = '',
  rating = {
    revs: 0,
    star_rating: 0,
  },
  description,
  currency = '',
  cs_id = 0,
  resources = [],
  view = 'list',
  link = '',
  setCurrentMarker,
  monthlyPrice = [],
  transportation_links = [],
  country = '',
  amenities_ids = '',
  reviews_full = [],
 listingsCardPosition,
 listingsPageNumber
}) => {
  const { workspaceType } = useResultListContext();
  const {isMobile, isTablet} = screenStore;

  const onSpaceHandler = () => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'select_item',
        centre_city: formatCityName(getCityFromPath()), // hardcoded since you're on /united-kingdom/london
        centre_id: cs_id.toString(),
        centre_image_count: images.length,
        centre_price_private: getPrice(monthlyPrice, 'Private Office'),
        centre_price_airport: getPrice(monthlyPrice, 'Airport Office'),
        centre_price_coworking: getPrice(monthlyPrice, 'Coworking'),
        centre_price_virtual: getPrice(monthlyPrice, 'Virtual Office'),
        content_type: 'centre',
        currency: currency,
        listings_view_type: view
      });

      sessionStorage.setItem('isShowBackButton', 'true');
      window.location.href = link;
    }
    window.location.href = link;
    sessionStorage.setItem('isShowBackButton', 'true');
  };
  const onModalHandler = (e: any) => {
    e.stopPropagation();

    openModal(FormType.QuickQuote, e.target as HTMLElement, {
      cs_id,
      isPopular,
      ratingScore: rating.star_rating,
      listingsCardPosition,
      listingsPageNumber
    });
  };
  const blockedCountries = ["ukraine", "cuba", "iran", "north-korea", "syria", "belarus", "russia", "venezuela", "afghanistan"];
  const getPrice = (prices: MonthlyPrice[] | undefined, type: string): number => {
    const match = prices?.find(p => p.name.toLowerCase().includes(type.toLowerCase()));
    return match?.price || 0;
  };
  return (
    <div
      className={styles.container + ' single-space-card'}
      data-id={cs_id}
      id={view === 'map' ? styles.map : ''}
      onMouseEnter={() => setCurrentMarker(cs_id)}
      onMouseLeave={() => setCurrentMarker(null)}
    >
      {isPopular ? (
        <div className={styles.popularNow}>
          Popular{' '}
          <FontAwesomeIcon className={styles.container__icon} icon={faStar} />
        </div>
        ) : null
      }
      <Slider
        images={images}
        onSpaceHandler={onSpaceHandler}
        link={link}
      />
      <div className={styles.container__content} onClick={onSpaceHandler}>
        <a href={link} style={{width: "100%"}}>
          <p className={styles.container__content__title}>{header}</p>
        </a>
        {/* @ts-ignore */}
        {rating.revs > 0 && rating.star_rating !== null && rating.star_rating > 0 ? <Rating {...rating} /> : <div style={{height: window.CRO_899 == 3 ? '0' : '24px'}}/>}
        {/* @ts-ignore */}
        {window.CRO_899 == 3 && view === 'map' && !isTablet && !isMobile ? (
          <SpaceInfo
            resources={resources}
            view={view}
            monthlyPrice={monthlyPrice}
            transportation_links={transportation_links}
            country={country}
            description={description}
            amenities_ids={amenities_ids}
            reviews_full={reviews_full}
            header={header} />
        ) : (
          <>
            <Prices workspaceType={workspaceType} prices={monthlyPrice} resources={resources} />
            {(!isMobile && view === 'map') || view === 'list' ? (
              <>
                <TransportationComponent country={country} transportationLinks={transportation_links} />
                <div className={styles.spacer}></div>
              </>
            ) : null}
          </>
        )}
        <div data-space-id={cs_id}>
          <button
              className={`quickQuoteButton ${styles.container__content__button}`}
              data-name="booking-enquiry-submit"
              onClick={(e) => onModalHandler(e)}
              type="button"
              disabled={blockedCountries.includes(country?.toLowerCase())}
              style={
                blockedCountries.includes(country?.toLowerCase())
                    ? { backgroundColor: "grey", color: "white", cursor: "not-allowed" }
                    : {}
              }
          >
            Quick Quote
          </button>
        </div>
      </div>
    </div>
  );
};

export default observer(SearchResult);
